import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadFoodEntries, addFoodEntry, updateFoodEntry, deleteFoodEntry, loadFrequentFoods, loadWeights, loadSettings, saveSettings } from '../lib/grit-data.js';
import { macrosToKcal, bookPlan, dietBreakDue, addDays, DIET_BREAK_DAYS, BOOK_GOALS } from '../lib/nutrition.js';
import { localISO } from '../lib/phase.js';
import { viewing, isViewingSelf } from '../lib/auth.js';
import { icon } from '../components/icons.js';
import { toast } from '../components/toast.js';

const todayISO = () => localISO();
const fmtNum = (n) => Math.round(n).toLocaleString('en-US');
function dayLabel(iso) {
  if (iso === todayISO()) return 'Today';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function shiftDate(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
const hhmm = (ts) => { const d = new Date(ts); return `${((d.getHours() + 11) % 12) + 1}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() < 12 ? 'am' : 'pm'}`; };
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export function renderFood(el) {
  const me = viewing();
  const readOnly = !isViewingSelf();
  let date = todayISO();
  let entries = [], weights = [], settings = {}, weight = null, frequent = [];
  let detailsOpen = localStorage.getItem('grit-food-details') === '1';
  let focusKcal = false; // focus only after adding (no keyboard pop on open)
  let editingId = null; // food entry being edited inline
  let macrosEditing = false;

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5"><p class="text-inksoft">Loading…</p></div>${renderNav('food')}`;

  (async () => {
    [weights, settings, frequent] = await Promise.all([loadWeights(me), loadSettings(me), loadFrequentFoods(me)]);
    weight = weights.find((w) => w.weight_lb != null)?.weight_lb ?? null;
    if (weight != null) weight = Number(weight);
    entries = await loadFoodEntries(date, me);
    paint();
  })();

  async function reloadEntries() { [entries, frequent] = await Promise.all([loadFoodEntries(date, me), loadFrequentFoods(me)]); paint(); }

  // Book calories (BLS ch.9-10): one number a day from body weight. No resting-burn
  // formula and no eating back workouts: the multiplier already counts training.
  function inDietBreak() { return !!settings.diet_break_until && date <= settings.diet_break_until && (settings.diet_goal || 'cut') === 'cut'; }
  function currentPlan() {
    return bookPlan({ weightLb: weight, goal: settings.diet_goal || 'cut', activity: settings.activity_level || 'moderate', dietBreak: inDietBreak() });
  }

  function heroCard(plan, intake) {
    if (!plan.ready) {
      return `<div class="grit-card p-5 text-center">
        <div class="text-[17px] font-semibold text-ink">Log your weight to get your daily calories</div>
        <div class="text-[15px] text-inksoft mt-1">The book sets them from body weight (p.137). Weigh in on the Weight tab.</div>
        ${readOnly ? '' : '<a href="#/weight" class="grit-btn mt-4">Go to Weight</a>'}
      </div>`;
    }
    const left = plan.calories - intake;
    const over = left < 0;
    const frac = plan.calories ? intake / plan.calories : 0;
    const deg = Math.round(Math.max(0, Math.min(1, frac)) * 360);
    const ring = over ? '#B53B3B' : 'rgb(var(--brand))';
    const goalName = (BOOK_GOALS.find(([k]) => k === plan.goal) || [])[1] || 'Cut';
    return `<div class="grit-card p-5 flex flex-col items-center">
      <div class="w-40 h-40 rounded-full flex items-center justify-center" style="background:conic-gradient(${ring} ${deg}deg, rgba(118,118,128,0.14) 0)">
        <div class="w-[134px] h-[134px] rounded-full bg-card flex flex-col items-center justify-center">
          <div class="font-rounded font-bold tabular-nums leading-none ${over ? 'text-danger' : 'text-ink'}" style="font-size:38px;letter-spacing:-0.02em">${fmtNum(Math.abs(left))}</div>
          <div class="text-[15px] text-inksoft mt-1">${over ? 'over' : 'left'}</div>
        </div>
      </div>
      <div class="w-full mt-5 bg-pagebg rounded-2xl px-4">
        <div class="flex items-center justify-between min-h-[46px] text-[15px]"><span class="text-inksoft">${plan.dietBreak ? 'Diet break' : goalName} · ${plan.perLb} cal × ${fmtNum(plan.weightLb)} lb</span><b class="text-ink tabular-nums">${fmtNum(plan.calories)}</b></div>
        <div class="flex items-center justify-between min-h-[46px] text-[15px] border-t border-hairline"><span class="text-inksoft">Eaten</span><b class="text-ink tabular-nums">${fmtNum(intake)}</b></div>
      </div>
      <div class="text-[13px] text-inksoft mt-3 text-center">Same number every day. Your workouts are already counted in it (p.138, 160).</div>
    </div>`;
  }

  // Cut longer than 8 weeks: a 5-7 day break at maintenance, extra mostly carbs (p.152-153).
  function dietBreakCard() {
    if (readOnly || (settings.diet_goal || 'cut') !== 'cut' || date !== todayISO()) return '';
    if (inDietBreak()) {
      return `<div class="grit-card p-4 mt-3">
        <div class="text-[17px] font-semibold text-ink">Diet break through ${dayLabel(settings.diet_break_until)}</div>
        <div class="text-[15px] text-inksoft mt-1">Eating at maintenance, most of the extra from carbs. Training stays the same.</div>
        <button id="end-break" class="grit-btn-quiet mt-3">End the break early</button>
      </div>`;
    }
    const due = dietBreakDue({ goal: 'cut', cutStartISO: settings.diet_goal_started, lastBreakEndISO: settings.last_diet_break_end, todayISO: todayISO() });
    if (!due.due) return '';
    return `<div class="grit-card p-4 mt-3">
      <div class="text-[17px] font-semibold text-ink">Time for a diet break</div>
      <div class="text-[15px] text-inksoft mt-1">${due.weeks} weeks into this cut. The book has you eat at maintenance for 5 to 7 days, then cut again (p.152).</div>
      <button id="start-break" class="grit-btn mt-3">Start a 7-day break</button>
    </div>`;
  }

  // Daily macro totals vs goals, with inline goal editing.
  function macrosCard(totals, target) {
    const plan = currentPlan();
    const custom = !!settings.macro_custom;
    const goals = (!custom && plan.ready)
      ? { protein: plan.proteinG, carbs: plan.carbsG, fat: plan.fatG }
      : { protein: Number(settings.macro_protein_g) || 0, carbs: Number(settings.macro_carbs_g) || 0, fat: Number(settings.macro_fat_g) || 0 };
    const hasGoals = goals.protein || goals.carbs || goals.fat;
    if (macrosEditing && !readOnly) {
      const suggest = plan.ready ? `<button id="mg-suggest" class="w-full mt-2 min-h-[44px] text-[15px] font-semibold text-grit">Use the book's numbers</button>` : '';
      return `<div class="grit-card p-4 mt-3">
        <div class="text-[17px] font-semibold text-ink mb-3">Daily macro goals</div>
        <div class="grid grid-cols-3 gap-2">
          ${[['protein', 'Protein'], ['carbs', 'Carbs'], ['fat', 'Fat']].map(([k, l]) => `<label class="flex flex-col gap-1"><input id="mg-${k}" type="number" inputmode="numeric" value="${goals[k] || ''}" placeholder="0" class="grit-input text-center py-2.5 font-bold" /><span class="text-[12px] text-inksoft text-center">${l} g</span></label>`).join('')}
        </div>
        ${suggest}
        <div class="flex items-center gap-2 mt-3">
          <button id="mg-cancel" class="grit-btn-quiet flex-1">Cancel</button>
          <button id="mg-save" class="grit-btn flex-1">Save</button>
        </div>
      </div>`;
    }
    const bar = (label, val, goal, color) => {
      const pct = goal ? Math.min(100, Math.round((val / goal) * 100)) : 0;
      const over = goal && val > goal;
      return `<div class="py-2">
        <div class="flex items-center justify-between text-[15px]"><span class="font-semibold text-ink">${label}</span><span class="tabular-nums ${over ? 'text-danger font-semibold' : 'text-inksoft'}">${val}${goal ? ` of ${goal}` : ''} g</span></div>
        ${goal ? `<div class="h-2 rounded-full bg-fill mt-1.5 overflow-hidden"><div class="h-full rounded-full" style="width:${pct}%;background:${over ? '#B53B3B' : color}"></div></div>` : ''}
      </div>`;
    };
    return `<div class="grit-card p-4 mt-3">
      <div class="flex items-center justify-between mb-1">
        <div class="text-[17px] font-semibold text-ink">Macros today</div>
        ${readOnly ? '' : `<button id="macros-edit" class="text-grit text-[15px] font-semibold min-h-[44px] -my-2 px-1">${custom ? 'Edit goals' : 'Change'}</button>`}
      </div>
      ${bar('Protein', totals.protein, goals.protein, 'rgb(var(--brand))')}
      ${bar('Carbs', totals.carbs, goals.carbs, '#4B8BD6')}
      ${bar('Fat', totals.fat, goals.fat, '#15A36B')}
      ${hasGoals && !custom ? `<div class="text-[13px] text-inksoft mt-1">Protein ${plan.goal === 'cut' ? '1 g' : '0.9 g'} per pound, fat about a quarter of calories, carbs the rest (p.141-144).</div>` : ''}
      ${!hasGoals && !readOnly ? '<div class="text-[13px] text-inksoft mt-1">Log your weight to get the book\'s protein, carb and fat targets.</div>' : ''}
    </div>`;
  }

  function quickAdd() {
    if (readOnly) return '';
    // Quick tap (Kenny, 2026-10-25): log a snack in one tap, the same amounts as the lock-screen button.
    return `<div class="grit-card p-4 mt-3">
      <div class="grit-eyebrow mb-2">Quick tap</div>
      <div class="grid grid-cols-4 gap-2">
        ${[100, 250, 300].map((n) => `<button data-tap="${n}" class="min-h-[48px] rounded-2xl bg-grit-soft text-grit text-[17px] font-bold active:scale-95 transition-transform tabular-nums">${n}</button>`).join('')}
        <button id="tap-custom" class="min-h-[48px] rounded-2xl bg-fill text-ink text-[15px] font-semibold active:scale-95 transition-transform">Custom</button>
      </div>
      <input id="qnote" maxlength="80" placeholder="Note (optional), e.g. chips" aria-label="Note for a quick tap" class="grit-input mt-2" />
      <div class="h-px bg-hairline my-3"></div>
      <div class="flex items-center gap-2">
        <input id="kcal" type="number" inputmode="numeric" placeholder="Calories" class="grit-input flex-1 text-center text-xl font-bold" />
        <button id="addcal" class="grit-btn !w-auto px-7">Add</button>
      </div>
      <button id="toggle-details" class="text-grit text-[15px] font-semibold mt-1 min-h-[44px]">${detailsOpen ? 'Hide details' : 'Add a name and macros'}</button>
      ${detailsOpen ? `<div class="mt-2 space-y-2">
        <input id="flabel" placeholder="Label, e.g. sandwich" class="grit-input" />
        <div class="grid grid-cols-3 gap-2">
          ${['protein', 'carbs', 'fat'].map((k) => `<label class="flex flex-col items-center gap-1"><input id="m-${k}" type="number" inputmode="numeric" placeholder="0" class="grit-input w-full text-center py-2 font-bold" /><span class="text-[12px] text-inksoft">${k[0]}</span></label>`).join('')}
        </div>
      </div>` : ''}
    </div>`;
  }

  // One-tap re-log of foods eaten before (auto-built from history).
  function quickLog() {
    if (readOnly || !frequent.length) return '';
    return `<div class="mt-3">
      <div class="grit-eyebrow mb-1.5">Quick log</div>
      <div class="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5" style="scrollbar-width:none">
        ${frequent.map((f, i) => `<button data-quick="${i}" class="flex-none bg-card rounded-2xl px-4 py-2.5 text-left active:scale-[0.97] transition-transform">
          <div class="font-bold text-[15px] text-ink truncate max-w-[150px]">${esc(f.label)}</div>
          <div class="text-[12px] text-inksoft">${fmtNum(f.calories)} cal</div>
        </button>`).join('')}
      </div>
    </div>`;
  }

  function editRow(e) {
    const v = (x) => (x == null ? '' : x);
    return `<div class="grit-card p-3 mb-2">
      <div class="flex items-center gap-2">
        <input id="e-kcal" type="number" inputmode="numeric" value="${v(e.calories)}" placeholder="Calories" class="grit-input w-24 text-center font-bold" />
        <input id="e-label" value="${esc(e.label)}" placeholder="Label" class="grit-input flex-1 min-w-0" />
      </div>
      <div class="grid grid-cols-3 gap-2 mt-2">
        ${[['protein', 'protein_g'], ['carbs', 'carbs_g'], ['fat', 'fat_g']].map(([k, col]) => `<label class="flex flex-col items-center gap-1"><input id="e-${k}" type="number" inputmode="numeric" value="${v(e[col])}" placeholder="0" class="grit-input w-full text-center py-2 font-bold" /><span class="text-[12px] text-inksoft">${k[0]}</span></label>`).join('')}
      </div>
      <div class="flex items-center gap-2 mt-2">
        <button id="e-cancel" class="grit-btn-quiet flex-1">Cancel</button>
        <button id="e-save" class="grit-btn flex-1">Save</button>
      </div>
    </div>`;
  }

  function entryRows() {
    if (!entries.length) return `<div class="grit-card p-5 text-center text-[15px] text-inksoft">Nothing logged yet. Throw in your first meal above.</div>`;
    return entries.map((e) => {
      if (!readOnly && e.id === editingId) return editRow(e);
      const macros = [e.protein_g != null ? `${e.protein_g}P` : '', e.carbs_g != null ? `${e.carbs_g}C` : '', e.fat_g != null ? `${e.fat_g}F` : ''].filter(Boolean).join(' · ');
      const from = e.source === 'mfp' ? 'From MyFitnessPal' : e.source === 'quick' ? `Quick tap · ${hhmm(e.created_at)}` : '';
      const body = `<div class="font-bold text-[15px] text-ink truncate">${esc(e.label || 'Quick add')}</div><div class="text-[12px] text-inksoft">${[macros, from].filter(Boolean).join(' · ') || hhmm(e.created_at)}</div>`;
      return `<div class="grit-card p-3 mb-2 flex items-center gap-3">
        <div class="flex-none px-2.5 h-8 rounded-lg bg-grit-soft text-grit text-[15px] font-bold flex items-center">${fmtNum(e.calories)}</div>
        ${readOnly
          ? `<div class="flex-1 min-w-0">${body}</div>`
          : `<button data-edit="${e.id}" class="flex-1 min-w-0 text-left active:opacity-70">${body}</button>`}
        ${readOnly ? '' : `<button data-del="${e.id}" aria-label="Delete" class="flex-none w-11 h-11 -mr-2 flex items-center justify-center text-inksoft">${icon.x('w-4 h-4')}</button>`}
      </div>`;
    }).join('');
  }

  function paint() {
    const plan = currentPlan();
    const intake = entries.reduce((a, e) => a + Number(e.calories || 0), 0);
    const isToday = date === todayISO();
    const macros = entries.reduce((a, e) => ({
      protein: a.protein + (Number(e.protein_g) || 0),
      carbs: a.carbs + (Number(e.carbs_g) || 0),
      fat: a.fat + (Number(e.fat_g) || 0),
    }), { protein: 0, carbs: 0, fat: 0 });
    macros.protein = Math.round(macros.protein); macros.carbs = Math.round(macros.carbs); macros.fat = Math.round(macros.fat);

    el.innerHTML = `${renderHeader()}
      <div class="grit-page px-5 pt-5">
        <div class="flex items-end justify-between mb-4">
          <div><div class="grit-eyebrow">${dayLabel(date)}</div><h1 class="grit-title mt-0.5">Food</h1></div>
          <div class="flex items-center">
            <button id="d-prev" aria-label="Previous day" class="w-11 h-11 flex items-center justify-center text-grit">${icon.left('w-5 h-5')}</button>
            <button id="d-next" aria-label="Next day" ${isToday ? 'disabled' : ''} class="w-11 h-11 -mr-2 flex items-center justify-center text-grit disabled:opacity-30">${icon.right('w-5 h-5')}</button>
          </div>
        </div>
        ${heroCard(plan, intake)}
        ${dietBreakCard()}
        ${macrosCard(macros)}
        ${quickAdd()}
        ${quickLog()}
        <div class="grit-section mt-6 mb-2">${dayLabel(date) === 'Today' ? "Today's food" : dayLabel(date)}</div>
        ${entryRows()}
        <div class="h-24"></div>
      </div>${renderNav('food')}`;

    document.getElementById('d-prev').onclick = () => { date = shiftDate(date, -1); editingId = null; macrosEditing = false; reloadEntries(); };
    const dn = document.getElementById('d-next'); if (dn) dn.onclick = () => { if (date !== todayISO()) { date = shiftDate(date, 1); editingId = null; macrosEditing = false; reloadEntries(); } };

    const addBtn = document.getElementById('addcal');
    if (addBtn) addBtn.onclick = () => handleAdd();
    const kcalInput = document.getElementById('kcal');
    if (kcalInput) {
      kcalInput.onkeydown = (ev) => { if (ev.key === 'Enter') handleAdd(); };
      if (focusKcal) { focusKcal = false; kcalInput.focus(); }
    }
    const td = document.getElementById('toggle-details');
    if (td) td.onclick = () => { detailsOpen = !detailsOpen; localStorage.setItem('grit-food-details', detailsOpen ? '1' : '0'); paint(); };
    el.querySelectorAll('[data-del]').forEach((b) => { b.onclick = () => handleDelete(b.dataset.del); });
    el.querySelectorAll('[data-edit]').forEach((b) => { b.onclick = () => { editingId = b.dataset.edit; paint(); }; });
    el.querySelectorAll('[data-quick]').forEach((b) => { b.onclick = () => handleQuickLog(+b.dataset.quick); });
    el.querySelectorAll('[data-tap]').forEach((b) => { b.onclick = () => handleTap(+b.dataset.tap); });
    const tc = document.getElementById('tap-custom'); if (tc) tc.onclick = () => { const k = document.getElementById('kcal'); k.focus(); k.scrollIntoView({ block: 'center' }); };
    const eSave = document.getElementById('e-save'); if (eSave) eSave.onclick = () => handleEditSave(editingId);
    const eCancel = document.getElementById('e-cancel'); if (eCancel) eCancel.onclick = () => { editingId = null; paint(); };
    const mEdit = document.getElementById('macros-edit'); if (mEdit) mEdit.onclick = () => { macrosEditing = true; paint(); };
    const mCancel = document.getElementById('mg-cancel'); if (mCancel) mCancel.onclick = () => { macrosEditing = false; paint(); };
    const mSave = document.getElementById('mg-save'); if (mSave) mSave.onclick = () => handleMacroGoals();
    const mSuggest = document.getElementById('mg-suggest'); if (mSuggest) mSuggest.onclick = () => handleBookMacros();
    const sb = document.getElementById('start-break'); if (sb) sb.onclick = () => handleDietBreak(true);
    const eb = document.getElementById('end-break'); if (eb) eb.onclick = () => handleDietBreak(false);
  }

  async function handleAdd() {
    if (readOnly) return;
    const kcalEl = document.getElementById('kcal');
    let kcal = kcalEl.value === '' ? null : Number(kcalEl.value);
    const labelEl = document.getElementById('flabel');
    const p = document.getElementById('m-protein')?.value;
    const c = document.getElementById('m-carbs')?.value;
    const f = document.getElementById('m-fat')?.value;
    if ((kcal == null || kcal === 0) && (p || c || f)) kcal = macrosToKcal(p, c, f); // macros-first still satisfies calories-required
    if (kcal == null || kcal <= 0 || Number.isNaN(kcal)) { kcalEl.focus(); return; }
    const entry = { date, calories: kcal, label: labelEl?.value || document.getElementById('qnote')?.value.trim() || null, protein_g: p ? Number(p) : null, carbs_g: c ? Number(c) : null, fat_g: f ? Number(f) : null };
    const saved = await addFoodEntry(entry);
    if (!saved) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    toast(`Logged ${fmtNum(kcal)}`);
    focusKcal = true;
    await reloadEntries();
  }

  async function handleTap(n) {
    if (readOnly || !n) return;
    const note = document.getElementById('qnote')?.value.trim().slice(0, 80) || null;
    const saved = await addFoodEntry({ date, calories: n, label: note || 'Quick add', source: 'quick' });
    if (!saved) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    const plan = currentPlan();
    const eaten = entries.reduce((a, e) => a + Number(e.calories || 0), 0) + n;
    toast(plan.ready ? `Logged ${n}. ${fmtNum(Math.max(0, plan.calories - eaten))} left` : `Logged ${n}`);
    await reloadEntries();
  }

  async function handleDelete(id) {
    if (readOnly) return;
    if (editingId === id) editingId = null;
    await deleteFoodEntry(id);
    await reloadEntries();
  }

  async function handleQuickLog(i) {
    if (readOnly) return;
    const f = frequent[i];
    if (!f) return;
    const saved = await addFoodEntry({ date, calories: f.calories, label: f.label, protein_g: f.protein_g ?? null, carbs_g: f.carbs_g ?? null, fat_g: f.fat_g ?? null });
    if (!saved) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    toast(`Logged ${f.label}`);
    await reloadEntries();
  }

  async function handleEditSave(id) {
    if (readOnly || !id) return;
    const kcalEl = document.getElementById('e-kcal');
    const kcal = Number(kcalEl.value);
    if (!kcal || kcal <= 0 || Number.isNaN(kcal)) { kcalEl.focus(); return; }
    const p = document.getElementById('e-protein').value;
    const c = document.getElementById('e-carbs').value;
    const f = document.getElementById('e-fat').value;
    const ok = await updateFoodEntry(id, {
      calories: kcal,
      label: document.getElementById('e-label').value || null,
      protein_g: p ? Number(p) : null, carbs_g: c ? Number(c) : null, fat_g: f ? Number(f) : null,
    });
    if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    editingId = null;
    toast('Updated');
    await reloadEntries();
  }

  async function handleMacroGoals() {
    if (readOnly) return;
    const num = (id) => { const v = document.getElementById(id)?.value; return v ? Number(v) : null; };
    const patch = { macro_protein_g: num('mg-protein'), macro_carbs_g: num('mg-carbs'), macro_fat_g: num('mg-fat'), macro_custom: true };
    Object.assign(settings, patch);
    const ok = await saveSettings(patch);
    if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    macrosEditing = false;
    toast('Macro goals saved');
    paint();
  }

  // Back to the book's numbers (they follow your weight from then on).
  async function handleBookMacros() {
    if (readOnly) return;
    settings.macro_custom = false;
    const ok = await saveSettings({ macro_custom: false });
    if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    macrosEditing = false;
    toast("Using the book's numbers");
    paint();
  }

  async function handleDietBreak(start) {
    if (readOnly) return;
    const patch = start
      ? { diet_break_until: addDays(todayISO(), DIET_BREAK_DAYS - 1) }
      : { diet_break_until: null, last_diet_break_end: todayISO() };
    if (start) patch.last_diet_break_end = addDays(todayISO(), DIET_BREAK_DAYS - 1);
    Object.assign(settings, patch);
    const ok = await saveSettings(patch);
    if (!ok) { toast("Couldn't save, try again", { tone: 'bad' }); return; }
    toast(start ? 'Diet break started' : 'Back to the cut');
    paint();
  }
}
